import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../shared/store/store';
import { categoryService } from '../services/categoryService';
import { CategoryState } from '../types/category.types';

const getToken = (state: RootState): string => state.auth.accessToken ?? '';

// ── Thunks ─────────────────────────────────────────────────────────────

export const fetchCategories = createAsyncThunk(
    'sellerCategory/fetchAll',
    async (params: Record<string, any>, { getState }) => {
        const { data } = await categoryService.getCategories(params, getToken(getState() as RootState));
        return data;
    }
);

export const createCategory = createAsyncThunk(
    'sellerCategory/create',
    async (formData: FormData, { getState }) => {
        const { data } = await categoryService.createCategory(formData, getToken(getState() as RootState));
        return data.category;
    }
);

export const updateCategory = createAsyncThunk(
    'sellerCategory/update',
    async ({ id, formData }: { id: string; formData: FormData }, { getState }) => {
        const { data } = await categoryService.updateCategory(id, formData, getToken(getState() as RootState));
        return data.category;
    }
);

export const deleteCategory = createAsyncThunk(
    'sellerCategory/delete',
    async (id: string, { getState }) => {
        await categoryService.deleteCategory(id, getToken(getState() as RootState));
        return id;
    }
);

export const toggleCategoryStatus = createAsyncThunk(
    'sellerCategory/toggleStatus',
    async ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }, { getState }) => {
        await categoryService.toggleCategoryStatus(id, status, getToken(getState() as RootState));
        return { id, status };
    }
);

export const fetchSubcategories = createAsyncThunk(
    'sellerCategory/fetchSubcategories',
    async (params: Record<string, any>, { getState }) => {
        const { data } = await categoryService.getSubcategories(params, getToken(getState() as RootState));
        return data;
    }
);

export const createSubcategory = createAsyncThunk(
    'sellerCategory/createSubcategory',
    async (payload: { categoryId: string; name: string }, { getState }) => {
        const { data } = await categoryService.createSubcategory(payload, getToken(getState() as RootState));
        return data.subcategory;
    }
);

export const updateSubcategory = createAsyncThunk(
    'sellerCategory/updateSubcategory',
    async ({ id, body }: { id: string; body: { name?: string; status?: string } }, { getState }) => {
        const { data } = await categoryService.updateSubcategory(id, body, getToken(getState() as RootState));
        return data.subcategory;
    }
);

export const deleteSubcategory = createAsyncThunk(
    'sellerCategory/deleteSubcategory',
    async (id: string, { getState }) => {
        await categoryService.deleteSubcategory(id, getToken(getState() as RootState));
        return id;
    }
);

export const toggleSubcategoryStatus = createAsyncThunk(
    'sellerCategory/toggleSubcategoryStatus',
    async ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }, { getState }) => {
        await categoryService.toggleSubcategoryStatus(id, status, getToken(getState() as RootState));
        return { id, status };
    }
);

// ── Slice ──────────────────────────────────────────────────────────────

const initialState: CategoryState = {
    categories      : [],
    subcategories   : [],
    total           : 0,
    categoryTotal   : 0,
    subcategoryTotal: 0,
    page            : 1,
    limit           : 10,
    loading         : false,
    error           : null,
};

const categorySlice = createSlice({
    name: 'sellerCategory',
    initialState,
    reducers: {},
    extraReducers: (builder) => {

        builder
            .addCase(fetchCategories.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchCategories.fulfilled, (s, a) => {
                s.loading = false;
                s.categories = a.payload.categories;
                s.total = a.payload.total;
                s.categoryTotal = a.payload.total;
                s.page  = a.payload.page;
            })
            .addCase(fetchCategories.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(createCategory.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(createCategory.fulfilled, (s, a) => {
                s.loading = false;
                s.categories.unshift(a.payload);
            })
            .addCase(createCategory.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(updateCategory.fulfilled, (s, a) => {
                const idx = s.categories.findIndex(c => c._id === a.payload._id);
                if (idx !== -1) s.categories[idx] = a.payload;
            });

        builder
            .addCase(deleteCategory.fulfilled, (s, a) => {
                s.categories = s.categories.filter(c => c._id !== a.payload);
            });

        builder
            .addCase(toggleCategoryStatus.fulfilled, (s, a) => {
                const cat = s.categories.find(c => c._id === a.payload.id);
                if (cat) cat.status = a.payload.status;
            });

        builder
            .addCase(fetchSubcategories.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchSubcategories.fulfilled, (s, a) => {
                s.loading = false;
                s.subcategories = a.payload.subcategories;
                s.total = a.payload.total;
                s.subcategoryTotal = a.payload.total;
                s.page  = a.payload.page;
            })
            .addCase(fetchSubcategories.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(createSubcategory.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(createSubcategory.fulfilled, (s, a) => {
                s.loading = false;
                s.subcategories.unshift(a.payload);
            })
            .addCase(createSubcategory.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(updateSubcategory.fulfilled, (s, a) => {
                const idx = s.subcategories.findIndex(sc => sc._id === a.payload._id);
                if (idx !== -1) s.subcategories[idx] = a.payload;
            });

        builder
            .addCase(deleteSubcategory.fulfilled, (s, a) => {
                s.subcategories = s.subcategories.filter(sc => sc._id !== a.payload);
            });

        builder
            .addCase(toggleSubcategoryStatus.fulfilled, (s, a) => {
                const sc = s.subcategories.find(sub => sub._id === a.payload.id);
                if (sc) sc.status = a.payload.status;
            });
    },
});

export default categorySlice.reducer;
